import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormattedMessage } from '../../../util/reactIntl';
import css from './ApplicationForm.module.css';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * @component RankingField
 * @param {object} props - The component's props.
 * @param {object} props.input - Properties injected by Final Form, containing `value` (current value) and `onChange` (callback to update the value).
 * @param {string} props.label - The internationalization (intl) ID for the component's label.
 * @param {Array<object>} props.options - An array of objects representing the items to be ranked. Each object must have a unique `value` and a `label`.
 * @param {boolean} props.readOnly - If `true`, disables drag-and-drop functionality, making the component read-only.
 * @returns {JSX.Element} The ranking field component.
 */

const RankingField = ({ input, label, description, options, readOnly }) => {
  const items = input.value && input.value.length === options.length
    ? input.value
    : options;

  const onDragEnd = (result) => {
    if (!result.destination || readOnly) {
      return;
    }

    const { source, destination } = result;

    const reorderedItems = reorder(
      items,
      source.index,
      destination.index
    );

    input.onChange(reorderedItems);
  };

  return (
    <div className={css.rankingContainer}>
      <h4 className={css.rankingLabel}>
        <FormattedMessage id={label} />
      </h4>
      {
        description ? (
          <p className={css.rankingDescription}>
            <FormattedMessage id={description} />
          </p>
        ) : null
      }
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="ranking-list">
          {(provided) => (
            <div
              className={css.rankingList}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {items.map((item, index) => (
                <Draggable key={item.value} draggableId={item.value} index={index} isDragDisabled={readOnly}>
                  {(provided, snapshot) => (
                    <div
                      className={css.rankingItem}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                      }}
                    >
                      <span className={css.dragHandle}>
                        <DragIndicatorIcon />
                      </span>
                      <span className={css.itemContent}>
                        <FormattedMessage id={item.label} />
                      </span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default RankingField;